/**
 * 按后端给的实体区间把正文切成 React 节点。
 * 偏移是 code point 口径，所以用 Array.from 切片。
 * 全程用 React 节点，绝不 dangerouslySetInnerHTML。
 */

import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

import type { Entities } from '@/api/types'

type Segment =
  | { kind: 'text'; text: string }
  | { kind: 'hashtag'; text: string; raw: string }
  | { kind: 'mention'; username: string; raw: string }
  | { kind: 'url'; url: string; display: string }

export function splitByEntities(content: string, entities: Entities): Segment[] {
  const chars = Array.from(content)

  type Marked = { start: number; end: number; build: (raw: string) => Segment }
  const marks: Marked[] = [
    ...entities.hashtags.map((e) => ({
      start: e.start,
      end: e.end,
      build: (raw: string): Segment => ({ kind: 'hashtag', text: e.text, raw }),
    })),
    ...entities.mentions.map((e) => ({
      start: e.start,
      end: e.end,
      build: (raw: string): Segment => ({ kind: 'mention', username: e.username, raw }),
    })),
    ...entities.urls.map((e) => ({
      start: e.start,
      end: e.end,
      build: (): Segment => ({ kind: 'url', url: e.url, display: e.display }),
    })),
  ].sort((a, b) => a.start - b.start)

  const segments: Segment[] = []
  let cursor = 0

  for (const mark of marks) {
    // 越界或重叠的区间直接跳过，宁可当普通文本也不要错位
    if (mark.start < cursor || mark.end > chars.length || mark.start >= mark.end) continue

    if (mark.start > cursor) {
      segments.push({ kind: 'text', text: chars.slice(cursor, mark.start).join('') })
    }
    const raw = chars.slice(mark.start, mark.end).join('')
    segments.push(mark.build(raw))
    cursor = mark.end
  }

  if (cursor < chars.length) {
    segments.push({ kind: 'text', text: chars.slice(cursor).join('') })
  }
  return segments
}

const linkClass = 'text-brand-ink hover:underline'

export function renderContent(content: string | null, entities: Entities): ReactNode {
  if (!content) return null

  return splitByEntities(content, entities).map((segment, index) => {
    switch (segment.kind) {
      case 'hashtag':
        return (
          <Link
            key={index}
            to={`/hashtag/${encodeURIComponent(segment.text)}`}
            className={linkClass}
            onClick={(event) => event.stopPropagation()}
          >
            {segment.raw}
          </Link>
        )
      case 'mention':
        return (
          <Link
            key={index}
            to={`/${segment.username}`}
            className={linkClass}
            onClick={(event) => event.stopPropagation()}
          >
            {segment.raw}
          </Link>
        )
      case 'url':
        return (
          <a
            key={index}
            href={segment.url}
            target="_blank"
            rel="noopener noreferrer nofollow ugc"
            className={linkClass}
            onClick={(event) => event.stopPropagation()}
          >
            {segment.display}
          </a>
        )
      default:
        return <span key={index}>{segment.text}</span>
    }
  })
}

/** Composer 的字数统计：与后端一致按 code point 计 */
export function countChars(text: string): number {
  return Array.from(text).length
}
